import {
  isNil,
  isArray,
  isPlainObject,
  isEmpty,
  forEach,
  uniq,
  flatMap,
  keys,
  get,
  assign,
} from 'lodash';

/**
 * Deeply flattens a nested object into dot-notation key-value pairs.
 *
 * @example
 * flattenObject({ user: { id: 1, name: "karl" } })
 * // => { "user.id": 1, "user.name": "karl" }
 *
 * flattenObject({ tags: [{ id: 1 }, { id: 2 }] })
 * // => { "tags.id": "1, 2" }
 *
 * flattenObject({ scores: [10, 20, 30] })
 * // => { scores: "10, 20, 30" }
 */
const flattenObject = (obj, prefix = '') => {
  const result = {};

  if (isNil(obj) || !isPlainObject(obj)) {
    return result;
  }

  forEach(obj, (value, key) => {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (isNil(value)) {
      result[newKey] = '';
    } else if (isArray(value)) {
      if (isEmpty(value)) {
        result[newKey] = '';
      } else if (isPlainObject(value[0])) {
        // Array of objects — collect each sub-key's values across all items
        const subKeys = uniq(
          flatMap(value, item =>
            isPlainObject(item) ? keys(item) : [],
          ),
        );
        forEach(subKeys, subKey => {
          result[`${newKey}.${subKey}`] = value
            .map(item => get(item, subKey, '') ?? '')
            .join(', ');
        });
      } else {
        // Array of primitives — join as comma-separated string
        result[newKey] = value.join(', ');
      }
    } else if (isPlainObject(value)) {
      // Nested object — recurse
      assign(result, flattenObject(value, newKey));
    } else {
      result[newKey] = value;
    }
  });

  return result;
};

export default flattenObject;
